// src/components/Project/ProjectUserSelector.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, TextField, Chip, CircularProgress, Box } from '@mui/material';
import { searchUsers, User, UserApiError } from '../../services/userApi';

// Simple debounce function
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((...args: any[]) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

interface ProjectUserSelectorProps {
  value: User[];
  onChange: (users: User[]) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  autoFocus?: boolean;
  allowedUserIds: Set<number>; // Only show users with these IDs
  projectUsers?: User[]; // Optional: actual project user objects for local filtering
}

/**
 * UserSelector component that filters to only show project users
 */
const ProjectUserSelector: React.FC<ProjectUserSelectorProps> = ({
  value = [],
  onChange,
  onBlur,
  error = false,
  helperText,
  placeholder = 'Search project users...',
  disabled = false,
  multiple = true,
  autoFocus = false,
  allowedUserIds,
  projectUsers = [],
}) => {
  const [options, setOptions] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Helper function to check if a user matches the search query
  const userMatchesQuery = useCallback((user: User, query: string): boolean => {
    const lowerQuery = query.toLowerCase();
    const displayName = (user.displayName || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return displayName.includes(lowerQuery) || email.includes(lowerQuery);
  }, []);

  // Filter project users locally by search query (optional filtering)
  const filterProjectUsersLocally = useCallback((query: string): User[] => {
    if (!projectUsers || projectUsers.length === 0) return [];
    
    // If no query, return all project users
    if (!query || query.trim() === '') {
      return projectUsers.filter(
        user => user.isActive && allowedUserIds.has(user.userId)
      );
    }
    
    // If query provided, filter by it
    return projectUsers.filter(
      user => user.isActive && allowedUserIds.has(user.userId) && userMatchesQuery(user, query)
    );
  }, [projectUsers, allowedUserIds, userMatchesQuery]);

  // Debounced search function - filters to only project users
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      setLoading(true);
      
      // Start with locally filtered project users (immediate results)
      const localMatches = filterProjectUsersLocally(query);
      const localUserIds = new Set(localMatches.map(u => u.userId));
      
      // If we have a query, also search via API for users that might not be in projectUsers yet
      if (query && query.trim().length > 0) {
        try {
          const response = await searchUsers(query, 50);
          // Filter to only active users that are in the project
          const apiMatches = response.users.filter(
            user => user.isActive && allowedUserIds.has(user.userId) && !localUserIds.has(user.userId)
          );
          
          // Combine local and API results, with local results first (they're guaranteed to be project users)
          const combinedResults = [...localMatches, ...apiMatches];
          setOptions(combinedResults);
        } catch (err) {
          console.error('Error searching users:', err);
          // If API fails, still show local matches if available
          setOptions(localMatches);
          // Check if error has the UserApiError structure
          if (err && typeof err === 'object' && 'code' in err && (err as UserApiError).code === 'AUTH_REQUIRED') {
            // Silently fail - user will see local results if available
          }
        } finally {
          setLoading(false);
        }
      } else {
        // No query - just show all local project users
        setOptions(localMatches);
        setLoading(false);
      }
    }, 300),
    [allowedUserIds, filterProjectUsersLocally]
  );

  // Load all project users on mount and when projectUsers/allowedUserIds change
  useEffect(() => {
    // Always show all project users initially (no query required)
    const allProjectUsers = projectUsers.filter(
      user => user.isActive && allowedUserIds.has(user.userId)
    );
    setOptions(allProjectUsers);
  }, [projectUsers, allowedUserIds]);

  // Search when input changes
  useEffect(() => {
    if (inputValue) {
      debouncedSearch(inputValue);
    } else {
      // When input is cleared, show all project users again
      const allProjectUsers = projectUsers.filter(
        user => user.isActive && allowedUserIds.has(user.userId)
      );
      setOptions(allProjectUsers);
    }
  }, [inputValue, debouncedSearch, projectUsers, allowedUserIds]);

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  // Track if we're currently selecting an option to prevent premature onBlur
  const isSelectingRef = useRef(false);

  const handleChange = (_event: React.SyntheticEvent, newValue: User | User[] | null) => {
    isSelectingRef.current = true;
    const users = multiple ? ((newValue as User[]) || []) : (newValue ? [newValue as User] : []);
    onChange(users);
    // Reset the flag after a short delay to allow the selection to complete
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  const handleBlur = () => {
    // Don't trigger onBlur if we're in the middle of selecting an option
    if (isSelectingRef.current) {
      return;
    }
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      value={multiple ? value : (value.length > 0 ? value[0] : null)}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      loading={loading}
      disabled={disabled}
      autoFocus={autoFocus}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.displayName || option.email;
      }}
      isOptionEqualToValue={(option, val) => option.userId === val.userId}
      filterOptions={(x) => x} // Disable client-side filtering, we use server-side search
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={error}
          helperText={helperText || 'Only project users can be assigned'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.userId}
            label={option.displayName || option.email}
            size="small"
          />
        ))
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.userId}>
          <Box>
            <Box sx={{ fontWeight: 500 }}>{option.displayName || option.email}</Box>
            {option.displayName && (
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{option.email}</Box>
            )}
          </Box>
        </Box>
      )}
      noOptionsText={inputValue && inputValue.trim().length > 0 ? 'No project users found' : 'No project users available'}
    />
  );
};

export default ProjectUserSelector;

